import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface WelcomeScreenProps {
  firstName: string;
  selectedStyles: string[];
  selectedCompanions: string[];
}

const STYLE_MESSAGES: Record<string, string> = {
  beach: 'beachfront places',
  mountain: 'mountain chalets',
  city: 'city apartments',
  village: 'village stone houses',
};

export const WelcomeScreen: React.FC<WelcomeScreenProps> = ({
  firstName,
  selectedStyles,
  selectedCompanions,
}) => {
  const navigate = useNavigate();
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setShowContent(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Build personalized message
  let message = "We're so glad you're here.";
  if (selectedStyles.length > 0) {
    const styleTexts = selectedStyles.slice(0, 2).map((s) => STYLE_MESSAGES[s] || s);
    message = `We'll find you ${styleTexts.join(' and ')} you'll love.`;
  } else if (selectedCompanions.length > 0) {
    message = "We'll help you find the perfect stay for every trip.";
  }

  return (
    <div className="min-h-screen relative flex items-center justify-center overflow-hidden bg-gradient-to-br from-primary-50 via-white to-primary-100">
      {/* CSS Confetti */}
      <div className="confetti-container" aria-hidden="true">
        {Array.from({ length: 40 }).map((_, i) => (
          <div
            key={i}
            className="confetti-piece"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 3}s`,
              backgroundColor: [
                '#91c9ac', '#7ab896', '#63a680',
                '#f59e0b', '#3b82f6', '#ef4444',
                '#8b5cf6', '#ec4899',
              ][i % 8],
            }}
          />
        ))}
      </div>

      {/* Confetti CSS */}
      <style>{`
        .confetti-container {
          position: absolute;
          inset: 0;
          pointer-events: none;
          overflow: hidden;
        }
        .confetti-piece {
          position: absolute;
          top: -10px;
          width: 10px;
          height: 10px;
          border-radius: 2px;
          opacity: 0;
          animation: confetti-fall linear forwards;
        }
        @keyframes confetti-fall {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
      `}</style>

      {/* Content */}
      <div
        className={`relative z-10 text-center px-6 max-w-md mx-auto transition-all duration-700 ${
          showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {/* Celebration icon */}
        <div className="text-6xl mb-6">
          {'\uD83C\uDF89'}
        </div>

        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
          Welcome to DARAK, {firstName}!
        </h1>

        <p className="text-lg text-gray-600 mb-10">
          {message}
        </p>

        <div className="space-y-3">
          <button
            type="button"
            onClick={() => navigate('/search')}
            className="w-full py-3 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-colors text-base"
          >
            Explore Properties
          </button>
          <button
            type="button"
            onClick={() => navigate('/dashboard/profile')}
            className="w-full py-3 border-2 border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors text-base"
          >
            Complete Profile
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeScreen;
